// The microphone chosen for recording on this computer, remembered per
// browser (device ids are per browser and origin) so the choice survives a
// reload and a device list refresh instead of going back to the default.

const STORAGE_KEY = "rosc.recordingMicrophone";

const storage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const getRecordingMicrophoneId = (): string | null => {
  try {
    return storage()?.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

// Null: the default microphone.
export const setRecordingMicrophoneId = (deviceId: string | null): void => {
  try {
    if (deviceId) storage()?.setItem(STORAGE_KEY, deviceId);
    else storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked (private mode): the choice lasts for this page only.
  }
};

// Chrome's "Default" and "Communications" entries follow the system's
// device; they are not one microphone, so they are only ever a preference.
const SYSTEM_DEVICE_IDS = new Set(["default", "communications"]);

export const isSystemMicrophoneId = (deviceId: string): boolean =>
  SYSTEM_DEVICE_IDS.has(deviceId);

// The audio constraints for the recording microphone. A specific microphone
// is required exactly: a bare deviceId is only an "ideal" hint, and Chrome
// then records from the microphone chosen for the site in its own settings
// (the camera/microphone icon in the address bar) instead.
export const recordingAudioConstraints = (
  deviceId: string | null,
  processing: MediaTrackConstraints = {},
): MediaTrackConstraints => {
  if (!deviceId) return { ...processing };
  return {
    ...processing,
    deviceId: isSystemMicrophoneId(deviceId)
      ? { ideal: deviceId }
      : { exact: deviceId },
  };
};

// The device is gone or cannot be opened (not a permission problem).
const isMissingDevice = (error: unknown): boolean => {
  const name = (error as { name?: unknown } | null)?.name;
  return (
    name === "OverconstrainedError" ||
    name === "NotFoundError" ||
    name === "DevicesNotFoundError" ||
    name === "NotReadableError"
  );
};

export interface RecordingStream {
  stream: MediaStream;
  // The chosen microphone was missing; the default one is recording.
  fellBack: boolean;
}

// Opens the microphone for one recording: the chosen one exactly, or the
// default one when none is chosen or the chosen one cannot be opened.
// Permission errors are thrown.
export const openRecordingStream = async ({
  deviceId,
  processing,
  getUserMedia = (constraints) =>
    navigator.mediaDevices.getUserMedia(constraints),
}: {
  deviceId: string | null;
  processing?: MediaTrackConstraints;
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
}): Promise<RecordingStream> => {
  try {
    const stream = await getUserMedia({
      audio: recordingAudioConstraints(deviceId, processing),
    });
    return { stream, fellBack: false };
  } catch (error) {
    if (!deviceId || isSystemMicrophoneId(deviceId) || !isMissingDevice(error))
      throw error;
    const stream = await getUserMedia({
      audio: recordingAudioConstraints(null, processing),
    });
    return { stream, fellBack: true };
  }
};
